import { useCallback, useEffect, useRef, useState } from "react"

import { getLastSelectedClient } from "lib/user/get-last-selected-client"
import { useSearchParams } from "react-router-dom"
import { getCurrentUserClients } from "lib/user/use-user-clients"
import { resetActiveUserClient, setActiveUserClient } from "lib/user/set-user-active-client"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import useAuth from "minimals-template/components/@hooks/useAuth"
import { useAsyncWithStatus } from "lib/@hooks/useAsync"
import { WindowReload } from "event-definitions"

// FIQ-578: a client switch normally completes when the server pushes a
// SwitchUserUrl event back to this browser instance, which reloads the app.
// If that event is lost (long-poll channel down, server instance recycled)
// the user was previously left on a half-switched app. This fallback reloads
// locally after a grace period; if the server event arrives first the page
// has already reloaded and the timer dies with it.
const SWITCH_RELOAD_FALLBACK_MS = 4000

export function useSelectedClient() {
    const { isAuthenticated } = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        loading: clientsLoading,
        data: clients,
        error: clientsError,
        refetch: refetchClients,
    } = getCurrentUserClients.useResults.status()
    // Tracks that the clients query has actually started at least once, so a
    // null result after loading means "failed" rather than "not started yet"
    const clientsAttempted = useRef(false)
    if (clientsLoading) clientsAttempted.current = true
    const requiredClientFromParams = searchParams.get("requiredClient")
    const shareLinkSource = searchParams.get("share")
    const isShareLinkSession = !!shareLinkSource && shareLinkSource !== "clear"
    const [selectedClient, setSelectedClient] = useState(requiredClientFromParams || shareLinkSource)
    useEffect(() => {
        if (!isAuthenticated) return
        if (requiredClientFromParams) {
            searchParams.delete("requiredClient")
            setSearchParams(searchParams)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, requiredClientFromParams, setSearchParams])

    // Bumped by retry() to re-run the last-selected-client lookup after a failure
    const [retryToken, setRetryToken] = useState(0)

    const {
        loading,
        error,
        data: lastSelectedClient,
    } = useAsyncWithStatus(
        async () => {
            if (!isAuthenticated) return null
            if (isShareLinkSession) return null
            if (selectedClient) return selectedClient
            return getLastSelectedClient()
        },
        "uninitialised",
        [isAuthenticated, isShareLinkSession, selectedClient, retryToken]
    )

    let [selectedClientInitialised, setInitialised] = useState(false)
    if (
        (isShareLinkSession || (!loading && !error && lastSelectedClient !== "uninitialised")) &&
        selectedClientInitialised === false
    ) {
        setInitialised(true)
    }

    if (!isShareLinkSession && !loading) {
        if (clients && clients.length > 0) {
            if (
                selectedClientInitialised &&
                selectedClient &&
                !clients.find((client) => client.id === selectedClient)
            ) {
                setSelectedClient(null)
            }
        }
    }

    const executionId = useRef()
    useEffect(() => {
        executionId.current = Date.now()

        if (isShareLinkSession) {
            setSelectedClient(shareLinkSource)
            return
        }

        if (!isAuthenticated || loading || error) return

        const currentExecutionId = executionId.current

        if (!selectedClient && !requiredClientFromParams && lastSelectedClient) {
            setSelectedClient(lastSelectedClient)
            return
        }

        const shouldSetClientTo = requiredClientFromParams || lastSelectedClient

        if (!shouldSetClientTo) return

        setActiveUserClient(shouldSetClientTo)
            .then((success) => {
                if (executionId.current !== currentExecutionId) return
                if (success) {
                    setSelectedClient(shouldSetClientTo)
                } else {
                    console.error("Failed to set active client (1)")
                    setSelectedClient(null)
                }
            })
            .catch(() => {
                console.error("Failed to set active client (2)")
                setSelectedClient(null)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, lastSelectedClient, requiredClientFromParams, loading, error])

    // FIQ-578: while true the guard shows a "Switching Client" screen instead
    // of leaving the user interacting with a half-switched app
    const [switching, setSwitching] = useState(false)
    const fallbackReloadTimer = useRef()
    useEffect(() => () => clearTimeout(fallbackReloadTimer.current), [])

    const setClient = useCallback(
        async function setClientHandler(client) {
            if (!isAuthenticated) return
            if (isShareLinkSession) return
            // Only a change away from an already-active client is a "switch"
            // (initial selection from the chooser does not reload the app)
            const isSwitch = !!selectedClient && client !== selectedClient
            if (isSwitch) setSwitching(true)
            try {
                const success = await setActiveUserClient(client)
                if (success) {
                    setSelectedClient(client)
                    if (isSwitch) {
                        clearTimeout(fallbackReloadTimer.current)
                        fallbackReloadTimer.current = setTimeout(
                            () => WindowReload.raise("/app"),
                            SWITCH_RELOAD_FALLBACK_MS
                        )
                    }
                } else {
                    setSwitching(false)
                    errorSnackbar("Failed to set active client")
                }
            } catch (e) {
                setSwitching(false)
                errorSnackbar("Failed to set active client")
            }
        },
        [isAuthenticated, isShareLinkSession, selectedClient]
    )

    const retry = useCallback(
        function retryHandler() {
            setRetryToken((token) => token + 1)
            refetchClients()
        },
        [refetchClients]
    )

    const resetSelectedClient = useCallback(
        async function resetSelectedClientHandler() {
            if (!isAuthenticated) return
            if (isShareLinkSession) return
            try {
                const success = await resetActiveUserClient()
                if (success) {
                    setSelectedClient(null)
                } else {
                    errorSnackbar("Failed to reset active client")
                }
            } catch (e) {
                errorSnackbar("Failed to reset active client")
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [isAuthenticated, isShareLinkSession]
    )

    // FIQ-578: surfaced so the auth guard can show a recovery UI instead of an
    // indefinite spinner. Covers query errors AND the "silent null" case where
    // a network failure is swallowed and the clients query resolves empty.
    const clientsFailed = !clientsLoading && clientsAttempted.current && !clients
    const loadFailed = !isShareLinkSession && (!!error || !!clientsError || clientsFailed)

    return {
        selectedClientInitialised: isShareLinkSession || selectedClientInitialised,
        loading: isShareLinkSession ? false : loading,
        clientsLoading: isShareLinkSession ? false : clientsLoading,
        clients: isShareLinkSession ? [] : clients,
        setSelectedClient: setClient,
        selectedClient: isShareLinkSession ? shareLinkSource : selectedClient,
        resetSelectedClient,
        loadFailed,
        retry,
        switching,
    }
}
